import { z } from 'zod'

/**
 * Process-level cache of the Supabase project's JWT signing keys.
 *
 * `supabase.auth.getClaims()` verifies an access token locally against the
 * project's `/.well-known/jwks.json` instead of asking GoTrue about every single
 * request — but it caches those keys **on the client instance**
 * (`GoTrueClient.jwks`), and every server entry point here builds a fresh
 * `createServerClient` per request. Left alone that would just trade one GoTrue
 * round trip per request for one JWKS round trip per request.
 *
 * So the cache lives here instead, and the keys are handed to `getClaims()`
 * through its documented `options.jwks`, which takes precedence over the
 * client's own cache. Supabase's docs call out this exact situation for
 * ephemeral environments ("a network request will be sent for each new
 * invocation").
 *
 * Fetching the keys here rather than letting auth-js do it also keeps the error
 * classification ours: a failure to reach the JWKS endpoint returns `undefined`
 * and the caller falls back to server-side verification, instead of surfacing
 * as an auth error that could be mistaken for a rejected credential.
 */

// The keys are long-lived; this only bounds how long a rotation takes to be
// picked up, and an unknown `kid` forces a refresh before the TTL anyway.
const JWKS_CACHE_TTL_MS = 10 * 60 * 1000

// Short on purpose: a hung request here would add its own latency to every
// request that finds the cache cold.
const JWKS_FETCH_TIMEOUT_MS = 5_000

/**
 * How long a failed fetch suppresses the next attempt.
 *
 * Without it every request re-fetches for as long as keys are unavailable, and
 * two of the ways they can be unavailable are not transient at all: a project
 * still on the legacy shared JWT secret publishes `{ keys: [] }` *permanently*,
 * and an unreachable endpoint costs the full `JWKS_FETCH_TIMEOUT_MS` each time.
 * Either would put a fetch — up to a 5s one — in front of nearly every request,
 * on top of the `getUser()` round trip it then falls back to. That is worse than
 * the behavior this module exists to replace.
 *
 * Short enough that recovery is a matter of seconds; long enough that a
 * sustained failure costs two fetches a minute regardless of traffic.
 */
const JWKS_FAILURE_BACKOFF_MS = 30_000

// `looseObject`, not `object`: zod strips unknown keys, and the key *material*
// (`x`/`y`/`crv` for EC, `n`/`e` for RSA) lives in exactly those keys — stripping
// them would hand `crypto.subtle.importKey` an unusable key.
const jwkSchema = z.looseObject({
  kty: z.string(),
  key_ops: z.array(z.string()),
  alg: z.string().optional(),
  kid: z.string().optional(),
})

const jwksSchema = z.object({ keys: z.array(jwkSchema) })

export type Jwks = z.infer<typeof jwksSchema>

type CachedJwks = { jwks: Jwks; fetchedAt: number }

let cached: CachedJwks | undefined
let lastFailureAt: number | undefined
let inFlight: Promise<Jwks | undefined> | undefined

const isFresh = (entry: CachedJwks, now: number): boolean =>
  entry.fetchedAt + JWKS_CACHE_TTL_MS > now

const isBackingOff = (now: number): boolean =>
  lastFailureAt !== undefined && lastFailureAt + JWKS_FAILURE_BACKOFF_MS > now

/**
 * Keep serving the last keys we managed to fetch.
 *
 * Signing keys are long-lived, so a stale set is almost always still the right
 * one — and discarding it would trade a working local verification for the
 * GoTrue round trip this module exists to avoid, at exactly the moment the
 * project is already having trouble. If the keys really have rotated, the token
 * carries a `kid` this set does not contain, and auth-js's own `fetchJwk` falls
 * through to the endpoint itself; nothing is ever verified against a key that
 * does not match the token's `kid`.
 */
function recordFailure(): Jwks | undefined {
  lastFailureAt = Date.now()
  return cached?.jwks
}

async function fetchJwks(): Promise<Jwks | undefined> {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL
  const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
  if (!url || !anonKey) return undefined

  try {
    const response = await fetch(`${url}/auth/v1/.well-known/jwks.json`, {
      headers: { apikey: anonKey },
      signal: AbortSignal.timeout(JWKS_FETCH_TIMEOUT_MS),
    })
    if (!response.ok) return recordFailure()
    const parsed = jwksSchema.safeParse(await response.json())
    if (!parsed.success || parsed.data.keys.length === 0) return recordFailure()
    cached = { jwks: parsed.data, fetchedAt: Date.now() }
    lastFailureAt = undefined
    return parsed.data
  } catch {
    // Deliberately quiet: the caller falls back to verifying against GoTrue, so
    // this is a slow path rather than a failure, and it runs often enough that
    // logging would drown out real problems.
    return recordFailure()
  }
}

/**
 * The project's signing keys, or `undefined` when they could not be fetched.
 *
 * Concurrent callers share one fetch — a cold process handling a burst of
 * requests must not open a JWKS connection per request.
 */
export async function getSigningKeys(): Promise<Jwks | undefined> {
  const now = Date.now()
  if (cached && isFresh(cached, now)) return cached.jwks
  if (isBackingOff(now)) return cached?.jwks
  if (inFlight) return inFlight

  inFlight = fetchJwks().finally(() => {
    inFlight = undefined
  })
  return inFlight
}

/** Test seam only. */
export function clearSigningKeysCache(): void {
  cached = undefined
  lastFailureAt = undefined
  inFlight = undefined
}
